/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';
import { logger } from './utils/logger';

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */

export const msalConfig = {
    auth: {
        // clientId: '393e4b0c-e4ed-45bf-8fc9-310d49bb32d9', // This is the ONLY mandatory field that you need to supply.
        clientId: process.env.REACT_APP_AZURE_AD_CLIENT_ID || process.env.REACT_APP_AZURE_AD_CLIENT_ID || process.env.ENTRA_CLIENT_ID || process.env.REACT_APP_ENTRA_CLIENT_ID || 'fa24242c-338d-4914-93ef-40a0fe549c45', // This is the ONLY mandatory field that you need to supply.
        authority: process.env.ENTRA_AUTHORITY || process.env.REACT_APP_ENTRA_AUTHORITY || 'https://login.microsoftonline.com/7b3bde85-87ae-4b3e-8f0a-90d48588bf83/',
        // redirectUri: 'http://localhost:3000/', // Points to window.location.origin. You must register this URI on Microsoft Entra admin center/App Registration.
        redirectUri: process.env.ENTRA_REDIRECT_URI || process.env.REACT_APP_ENTRA_REDIRECT_URI || (typeof window !== 'undefined' ? window.location.origin + '/' : 'http://localhost:3000/'), // Points to window.location.origin. You must register this URI on Microsoft Entra admin center/App Registration.
        // knownAuthorities: ["AugustSaysApp.ciamlogin.com/eba457b0-3b68-4542-b114-4cff62fcb4d4"],
        postLogoutRedirectUri: process.env.ENTRA_REDIRECT_URI || process.env.REACT_APP_ENTRA_REDIRECT_URI || (typeof window !== 'undefined' ? window.location.origin + '/' : 'http://localhost:3000/'), // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        // Prevent popup loops by adding retry logic
        allowRedirectInIframe: false,
        windowHashTimeout: 6000,
        iframeHashTimeout: 6000,
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        logger.error(message);
                        return;
                    case LogLevel.Info:
                        logger.info(message);
                        return;
                    case LogLevel.Verbose:
                        logger.debug(message);
                        return;
                    case LogLevel.Warning:
                        logger.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [],
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
// export const silentRequest = {
//     scopes: ["openid", "profile"],
//     loginHint: "example@domain.net"
// };
